import { Injectable, OnInit, OnDestroy } from '@angular/core';
import { HttpClient, HttpResponse, HttpErrorResponse } from '@angular/common/http';

import { map, catchError, take, timeout } from 'rxjs/operators';
import { of, Observable, Subscription } from 'rxjs';

import { LoggingMessageService } from './logging-message.service';
import { DataResult } from '../models/data-result.mode';
import { AuthService } from './auth.service';
import { environment } from 'src/environments/environment';
import { SessionStorageService } from 'src/app/services/session-storage.service';
import { SubscriptionChangeService } from './subscription-change.service';


@Injectable({
    providedIn: 'root'
})
export class UserRegistrationService implements OnInit, OnDestroy {
    private $subscriptions: Subscription = new Subscription();
    private _NO_DATA = { message: '' };

    private _BASE_URL: string = environment.FederationApi;
    private _LOCAL_NAVIGATION_API = environment.LocalJsonApi;
    private _LOCAL_NAVIGATION_FILENAME = 'user-registrations';


    constructor(private http: HttpClient, private authService: AuthService, private logger: LoggingMessageService, 
        private sessionService: SessionStorageService,
        private subscriptionService: SubscriptionChangeService) {
    }

    ngOnInit(): void {

    }

    public getUserRegistrations(email: string, clientId: string): Observable<DataResult> {
      if (environment.UseServerForUserRegistration) {
          return this.getUserRegistrationsFromBusinessApi(email, clientId);
      } else {
          return this.getUserRegistrationsFromLocalApi(email, clientId);
      }
    }

    public getUserCommunities(email: string): Observable<DataResult> {
        if (environment.UseServerForClinicalUserRegistration) {
            return this.getUserCommunitiesFromBusinessApi(email);
        } else {
            return this.getUserCommunitiesFromLocalApi(email);
        }
      }

    private getUserRegistrationsFromLocalApi(email: string, clientId: string): Observable<DataResult> {

      return of(null);
    }

    private getUserRegistrationsFromBusinessApi(email: string, clientId: string): Observable<DataResult> {
        const webmethod = 'IdentityProvider/GetUserRegistrations';
        const media = 'application/json';
        const client = clientId.toLowerCase().trim();
        const uri = `${this._BASE_URL}${webmethod}?email=${email}&clientId=${client}`;

        this.logger.debug('user-registration.service', 'getUserRegistrations', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `calling url: ${uri}`, '');

        return this.http.get<any>(
            uri,
            {
                headers: { Accept: media },
                observe: 'response'
            }
        )
            .pipe(
                timeout(10000),
                map(response => {
                    let result: DataResult;
                    if (response instanceof HttpResponse) {
                        const respBody: any = response.body;
                        if (response.status === 200) {
                            result = new DataResult(response.ok, response.status, response.statusText, respBody);
                        } else if (response.status === 401) {
                            result = new DataResult(false, 401, response.statusText, respBody.message);
                        } else {
                            result = new DataResult(false, 400, response.statusText, respBody.message);
                        }
                    }
                    if (response instanceof HttpErrorResponse) {
                        result = new DataResult(false, 500, response.statusText, response.message);
                    }
                    return result;
                }),
                catchError(error => {
                    this.logger.warn('user-registration.service', 'getUserRegistrations', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  'Error caught', '');
                    const message = this.getErrorMessage(error);
                    return this.handleError(`getUserRegistrations failed`, message);
                }),
                take(1),
            );
    }

    private getUserCommunitiesFromLocalApi(email: string): Observable<DataResult> {

        const uri = `${this._LOCAL_NAVIGATION_API}clinical-user-registrations.json`;

        return this.http.get<any>(
            uri, { observe: 'response' }
        )
            .pipe(
                map(response => {
                    let result: DataResult;
                    if (response instanceof HttpResponse) {
                        if (response.status === 200) {
                            result = new DataResult(response.ok, response.status, response.statusText, response.body);
                        }
                    }
                    if (response instanceof HttpErrorResponse) {
                        result = new DataResult(response.ok, response.status, response.statusText, this._NO_DATA);
                    }

                    return result;
                }),
                catchError(error => {
                    return this.handleError(`getSingleHospitalUserNavigationFromLocalApi failed`, error);
                }),
                take(1)
            );
      }
  
      private getUserCommunitiesFromBusinessApi(email: string): Observable<DataResult> {
          const webmethod = 'IdentityProvider/GetClinicalUserRegistrations';
          const media = 'application/json';
          const uri = `${this._BASE_URL}${webmethod}?email=${email}`;
  
          this.logger.debug('user-registration.service', 'getUserCommunitiesFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString,  `calling url: ${uri}`, '');
  
          return this.http.get<any>(
              uri,
              {
                  headers: { Accept: media },
                  observe: 'response'
              }
          )
              .pipe(
                  timeout(10000),
                  map(response => {
                      let result: DataResult;
                      if (response instanceof HttpResponse) {
                          const respBody: any = response.body;
                          if (response.status === 200) {
                              result = new DataResult(response.ok, response.status, response.statusText, respBody);
                          } else if (response.status === 401) {
                              result = new DataResult(false, 401, response.statusText, respBody.message);
                          } else {
                              result = new DataResult(false, 400, response.statusText, respBody.message);
                          }
                      }
                      if (response instanceof HttpErrorResponse) {
                          result = new DataResult(false, 500, response.statusText, response.message);
                      }
                      return result;
                  }),
                  catchError(error => {
                      this.logger.warn('user-registration.service', 'getUserCommunitiesFromBusinessApi', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, 'Error caught', '');
                      const message = this.getErrorMessage(error);
                      return this.handleError(`getUserCommunitiesFromBusinessApi failed`, message);
                  }),
                  take(1),
              );
      }

    private getErrorMessage(error: any): string {
        let message = error.message;
        if ((error.error) && (error.error.message)) {
            message = error.error.message;
        }
        return message;
    }

    private handleError(operation: string, error: any) {
        let result: any;
        const response = this._NO_DATA;
        this.logger.warn('user-registration.service', 'handleError', this.subscriptionService.SelectedSubscription, this.subscriptionService.AvailableSubscriptionsString, `Operation:${operation}`, `Server Error:${error}`);
        response.message = error;
        const errorResult = new DataResult(false, 503, 'Service Unavailable error', response);
        result = errorResult;

        return of(result);
    }

    unsubscribe() {
        if (this.$subscriptions) {
            this.$subscriptions.unsubscribe();
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }
}

